"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export type VideoRecorderState =
  | "idle"
  | "requesting"
  | "previewing"
  | "recording"
  | "stopped";

export interface UseVideoRecorderReturn {
  state: VideoRecorderState;
  durationMs: number;
  videoBlob: Blob | null;
  videoMimeType: string;
  stream: MediaStream | null;
  cameras: MediaDeviceInfo[];
  selectedCameraId: string | null;
  startPreview: (deviceId?: string) => Promise<void>;
  selectCamera: (deviceId: string) => Promise<void>;
  startRecording: () => void;
  stopRecording: () => void;
  cancelRecording: () => void;
  reset: () => void;
}

const PREFERRED_MIME_TYPES = [
  "video/webm; codecs=vp9,opus",
  "video/webm; codecs=vp8,opus",
  "video/webm",
  "video/mp4",
];

function detectMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  return (
    PREFERRED_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? ""
  );
}

async function enumerateCameras(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === "videoinput");
}

export function useVideoRecorder(): UseVideoRecorderReturn {
  const [state, setState] = useState<VideoRecorderState>("idle");
  const [durationMs, setDurationMs] = useState(0);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoMimeType, setVideoMimeType] = useState<string>("");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCancelledRef = useRef(false);

  useEffect(() => {
    setVideoMimeType(detectMimeType());
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  const openStream = useCallback(
    async (deviceId?: string): Promise<MediaStream> => {
      const videoConstraint: MediaTrackConstraints | boolean = deviceId
        ? { deviceId: { exact: deviceId } }
        : true;
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraint,
        audio: true,
      });
      return mediaStream;
    },
    [],
  );

  const startPreview = useCallback(
    async (deviceId?: string) => {
      setState("requesting");
      isCancelledRef.current = false;
      try {
        const mediaStream = await openStream(deviceId);
        streamRef.current = mediaStream;
        setStream(mediaStream);

        // Enumerate cameras after permission is granted (labels become available)
        const cams = await enumerateCameras();
        setCameras(cams);

        const activeTrack = mediaStream.getVideoTracks()[0];
        const activeDeviceId =
          deviceId ??
          activeTrack?.getSettings().deviceId ??
          cams[0]?.deviceId ??
          null;
        setSelectedCameraId(activeDeviceId);

        setState("previewing");
      } catch (err: unknown) {
        stopStream();
        setState("idle");
        if (err instanceof DOMException) {
          if (err.name === "NotAllowedError")
            throw new Error("Camera permission denied");
          if (err.name === "NotFoundError") throw new Error("No camera found");
        }
        throw err;
      }
    },
    [openStream, stopStream],
  );

  const selectCamera = useCallback(
    async (deviceId: string) => {
      if (state !== "previewing") return;
      setState("requesting");
      stopStream();
      try {
        const mediaStream = await openStream(deviceId);
        streamRef.current = mediaStream;
        setStream(mediaStream);
        setSelectedCameraId(deviceId);
        setState("previewing");
      } catch {
        setState("idle");
      }
    },
    [state, openStream, stopStream],
  );

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;

    const mimeType = detectMimeType();
    const options = mimeType ? { mimeType } : undefined;
    const recorder = new MediaRecorder(streamRef.current, options);
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      if (isCancelledRef.current) return;
      const blob = new Blob(chunksRef.current, {
        type: mimeType || "video/webm",
      });
      setVideoBlob(blob);
      setVideoMimeType(mimeType);
      setState("stopped");
    };

    recorder.start(250);
    setState("recording");

    setDurationMs(0);
    timerRef.current = setInterval(() => {
      setDurationMs((ms) => ms + 1000);
    }, 1000);
  }, []);

  const stopRecording = useCallback(() => {
    clearTimer();
    mediaRecorderRef.current?.stop();
    stopStream();
  }, [clearTimer, stopStream]);

  const cancelRecording = useCallback(() => {
    clearTimer();
    isCancelledRef.current = true;
    chunksRef.current = [];
    if (mediaRecorderRef.current?.state !== "inactive") {
      mediaRecorderRef.current?.stop();
    }
    stopStream();
    setVideoBlob(null);
    setDurationMs(0);
    setCameras([]);
    setSelectedCameraId(null);
    setState("idle");
  }, [clearTimer, stopStream]);

  const reset = useCallback(() => {
    clearTimer();
    stopStream();
    setVideoBlob(null);
    setDurationMs(0);
    setCameras([]);
    setSelectedCameraId(null);
    setState("idle");
  }, [clearTimer, stopStream]);

  useEffect(() => {
    return () => {
      clearTimer();
      stopStream();
      isCancelledRef.current = true;
      if (mediaRecorderRef.current?.state !== "inactive") {
        mediaRecorderRef.current?.stop();
      }
    };
  }, [clearTimer, stopStream]);

  return {
    state,
    durationMs,
    videoBlob,
    videoMimeType,
    stream,
    cameras,
    selectedCameraId,
    startPreview,
    selectCamera,
    startRecording,
    stopRecording,
    cancelRecording,
    reset,
  };
}
